import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { getHeatmapData } from "@/lib/admin.functions";
import { DateRangePicker, defaultRange, type Range } from "@/components/admin/DateRange";
import { SectionTabs, ANALYTICS_TABS } from "@/components/admin/SectionTabs";

export const Route = createFileRoute("/_authenticated/admin/heat-maps")({
  component: HeatMapsPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function HeatMapsPage() {
  const fn = useServerFn(getHeatmapData);
  const [range, setRange] = useState<Range>(defaultRange(30));
  const [path, setPath] = useState("/");
  const [device, setDevice] = useState<"all" | "desktop" | "mobile" | "tablet">("all");
  const [view, setView] = useState<"clicks" | "rage" | "dead">("clicks");

  const { data, isLoading } = useQuery({
    queryKey: ["heatmap", range, path, device],
    queryFn: () => fn({ data: { ...range, path, device } }),
  });

  const points = useMemo(() => {
    if (!data) return [];
    if (view === "clicks") return data.clicks;
    if (view === "rage") return data.rage;
    return data.dead;
  }, [data, view]);

  return (
    <div className="space-y-6">
      <SectionTabs tabs={ANALYTICS_TABS} label="Analytics" />
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Behavior</p>
          <h1 className="font-serif text-3xl text-white mt-1">Heat Maps</h1>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <select value={path} onChange={(e) => setPath(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white max-w-xs">
          {(data?.availablePaths || [{ path: "/", count: 0 }]).map((p: any) => (
            <option key={p.path} value={p.path}>{p.path} ({p.count})</option>
          ))}
        </select>
        <Toggle value={device} onChange={(v) => setDevice(v as any)} options={[
          { v: "all", l: "All" }, { v: "desktop", l: "Desktop" }, { v: "mobile", l: "Mobile" }, { v: "tablet", l: "Tablet" },
        ]} />
        <Toggle value={view} onChange={(v) => setView(v as any)} options={[
          { v: "clicks", l: `Clicks (${data?.clicks.length ?? 0})` },
          { v: "rage", l: `Rage (${data?.rage.length ?? 0})` },
          { v: "dead", l: `Dead (${data?.dead.length ?? 0})` },
        ]} />
      </div>

      {isLoading || !data ? <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div> : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <p className="text-xs text-zinc-500 mb-2">Live overlay of <span className="text-zinc-300 font-mono">{path}</span> — {points.length} {view} events.</p>
            <HeatmapCanvas path={path} points={points} view={view} device={device} />
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Avg scroll depth</p>
              <p className="text-2xl font-serif text-white mt-1">{data.avgScroll}%</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="text-sm font-semibold text-white mb-2">Hot targets</h3>
              {data.hotTargets.length === 0 ? <p className="text-xs text-zinc-500">No data</p> : (
                <ul className="space-y-1.5 text-xs">
                  {data.hotTargets.map((t: any) => (
                    <li key={t.target} className="flex justify-between text-zinc-300"><span className="truncate pr-2">{t.target}</span><span className="text-amber-300">{t.count}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HeatmapCanvas({ path, points, view, device }: { path: string; points: { x: number; y: number }[]; view: "clicks" | "rage" | "dead"; device: "all" | "desktop" | "mobile" | "tablet" }) {
  const color = view === "clicks" ? "250, 204, 21" : view === "rage" ? "248, 113, 113" : "167, 139, 250";
  const src = typeof window !== "undefined" ? `${window.location.origin}${path}` : path;
  // Virtual viewport for the embedded preview based on device toggle
  const dims = device === "mobile"
    ? { w: 390, h: 2400, label: "Mobile · 390 wide", max: 360 }
    : device === "tablet"
    ? { w: 820, h: 3000, label: "Tablet · 820 wide", max: 560 }
    : { w: 1280, h: 3200, label: "Desktop · 1280 wide", max: 1280 };
  const frameStyle: React.CSSProperties = device === "all" || device === "desktop"
    ? { width: "100%", maxWidth: "100%" }
    : { width: `${dims.max}px`, maxWidth: "100%", margin: "0 auto" };
  return (
    <div style={frameStyle}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{dims.label} · scroll to explore</p>
      <a href={src} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200">Open page ↗</a>
    </div>
    <div className="relative w-full bg-zinc-900 rounded-lg overflow-y-auto overflow-x-hidden border border-white/5" style={{ height: "min(80vh, 900px)" }}>
      {/* Scaled inner canvas: full virtual page height, container scrolls */}
      <div
        className="relative"
        style={{
          width: "100%",
          // scaled height = dims.h * (containerWidth / dims.w); use aspect-ratio trick
          aspectRatio: `${dims.w} / ${dims.h}`,
        }}
      >
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ filter: "grayscale(0.4) brightness(0.55)" }}>
        <iframe
          src={src}
          title="Page preview"
          sandbox=""
          loading="lazy"
          tabIndex={-1}
          aria-hidden
          className="border-0 absolute top-0 left-0"
          style={{
            width: `${dims.w}px`,
            height: `${dims.h}px`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
          ref={(el) => {
            if (!el || !el.parentElement) return;
            const parent = el.parentElement;
            const set = () => {
              const r = parent.getBoundingClientRect();
              const s = r.width / dims.w;
              el.style.transform = `scale(${s})`;
              el.style.width = `${dims.w}px`;
              el.style.height = `${dims.h}px`;
            };
            set();
            const ro = new ResizeObserver(set);
            ro.observe(parent);
          }}
        />
      </div>
      {/* Dimming layer */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      {/* Heat blobs */}
      <div className="absolute inset-0 pointer-events-none" style={{ mixBlendMode: "screen" }}>
        {points.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: 120,
              height: 120,
              transform: "translate(-50%,-50%)",
              background: `radial-gradient(circle, rgba(${color},0.85) 0%, rgba(${color},0.45) 35%, rgba(${color},0) 70%)`,
            }}
          />
        ))}
      </div>
      {points.length === 0 && (
        <div className="absolute inset-0 grid place-items-center text-xs text-zinc-400 bg-black/30">
          No {view} events for these filters.
        </div>
      )}
    </div>
    </div>
    </div>
  );
}

function Toggle({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <div className="flex bg-white/[0.04] border border-white/10 rounded-full p-1">
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} className={`text-xs px-3 py-1.5 rounded-full ${value === o.v ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>{o.l}</button>
      ))}
    </div>
  );
}